import { defineComponent as _defineComponent } from 'vue'
import { storeToRefs } from 'pinia'
import { computed, getCurrentInstance, watch } from 'vue'

import HelpTooltip from '@/client/components/_atoms/HelpTooltip.vue'
import Tags from '@/client/components/_molecules/Tags.vue'
import {
  MessageNode,
  TextNode,
  useInstagramScenarioEditorStore
} from '@/client/features/instagram_instantwin_prize/hooks/instagramScenarioEditorStore'
import { TrackingService } from '@/client/services'


export default /*#__PURE__*/_defineComponent({
  __name: 'StepMessage',
  setup(__props) {

// @ts-nocheck TODO: 既存の型エラーを解消したら外す
const { proxy } = getCurrentInstance() as {
  proxy: {
    $notify: (arg: { title: string; customClass: string; duration: number }) => void
  }
}

// Store
const store = useInstagramScenarioEditorStore()
const { messages, canAddMessage, errors, campaignRunning } = storeToRefs(store)
const isAddMessageDisabled = computed(() => campaignRunning.value || !canAddMessage.value)

const showLimitMessage = computed(() => !campaignRunning.value && !canAddMessage.value)

const getFileExtension = (name: string): string => {
  const index = name.lastIndexOf('.')
  return index >= 0 ? name.slice(index).toLowerCase() : ''
}

const isSupportedImageExtension = (name: string): boolean => {
  return ['.jpg', '.jpeg', '.png'].includes(getFileExtension(name))
}

// Getter functions (ラップして公開)
const canDeleteMessage = (index: number): boolean => {
  return store.canDeleteMessage(index)
}

const canChangeToType = (index: number, newType: 'text' | 'media' | 'select' | 'card'): boolean => {
  return store.canChangeToType(index, newType)
}

const getTextNodeError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.text`] || null
}

const getQuestionError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.question`] || null
}

const getOptionError = (index: number, optionIndex: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.options.${optionIndex}`] || null
}

const hasSelectOptionsErrors = (index: number): boolean => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return false
  }

  // 選択肢数のエラーチェック
  if (step2Errors[`messages.${index}.options`]) {
    return true
  }

  // 各選択肢のエラーチェック
  const message = messages.value[index]
  if (message && message.type === 'select') {
    for (let i = 0; i < message.options.length; i++) {
      if (step2Errors[`messages.${index}.options.${i}`]) {
        return true
      }
    }
  }

  return false
}

const getOptionsCountError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.options`] || null
}

const getMediaNodeError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) return null
  return step2Errors[`messages.${index}.media`] || null
}

// メディアノードが画像かどうかを判定
const isImageFile = (message: MessageNode): boolean => {
  if (!message.url) {
    return false
  }
  // fileTypeプロパティがあれば優先的に使用（blob URLの場合）
  if (message.fileType) {
    return message.fileType === 'image'
  }
  // fileTypeがない場合はURLの拡張子で判定（サーバーからのURLの場合）
  const imageExtensions = ['.png', '.jpg', '.jpeg']
  const url = message.url.toLowerCase()
  return imageExtensions.some(ext => url.includes(ext)) || message.url.startsWith('data:image/')
}

// メディアファイル名を取得
const getMediaFileName = (message: MessageNode): string => {
  // fileNameフィールドがあればそれを優先
  if (message.fileName) {
    return message.fileName
  }

  // blob URLの場合は拡張子から生成
  if (message.url && message.url.startsWith('blob:')) {
    const fileType = message.fileType === 'image' ? 'png' : 'mp4'
    return `media.${fileType}`
  }
  // サーバーURLの場合はURLから抽出
  if (message.url) {
    const urlWithoutQuery = message.url.split('?')[0]
    const urlParts = urlWithoutQuery.split('/')
    const fileName = urlParts[urlParts.length - 1]

    return decodeURIComponent(fileName)
  }
  return 'media'
}

const getCardButtonPreviewLabel = (label: string | undefined): string => {
  const normalized = (label || '').trim()
  if (normalized === '') return 'ラベルテキスト'
  return normalized.slice(0, 20)
}

// Methods
const handleAddMessage = () => {
  store.addMessage()
  // メッセージ追加後にバリデーション
  store.validateStep2()
}

const handleDelete = (index: number) => {
  if (!confirm('メッセージを削除しますか？')) {
    return
  }
  store.deleteMessage(index)
  // メッセージ削除後にバリデーション
  store.validateStep2()
}

const handleTypeChange = (index: number, newType: 'text' | 'media' | 'select' | 'card') => {
  // 型変更時にストアへ反映
  store.updateMessageType(index, newType)
  // タイプ変更後にバリデーション
  store.validateStep2()
}

const handleTextChange = (index: number, newText: string) => {
  // テキストノードのテキスト更新
  if (messages.value[index] && messages.value[index].type === 'text') {
    const node = messages.value[index] as TextNode
    node.text = newText

    // リアルタイムバリデーション
    store.validateStep2()
  }
}

// メディアノード: ファイル選択ボタンクリック
const handleMediaSelectClick = (index: number) => {
  // 非表示のfile inputをクリック
  const fileInput = document.getElementById(`fileInput-${index}`) as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

// メディアノード: ファイル選択時のハンドラー
const handleMediaFileChange = async (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]

  if (!file) {
    return
  }

  // ファイル形式チェック
  const imageTypes = ['image/png', 'image/jpeg']
  const videoTypes = ['video/mp4']

  const isImage = imageTypes.includes(file.type)
  const isVideo = videoTypes.includes(file.type)

  if (!isImage && !isVideo) {
    alert('対応形式はpng、jpg、mp4です。')
    input.value = '' // 入力をクリア
    return
  }

  if (isImage && !isSupportedImageExtension(file.name)) {
    alert('対応形式はpng、jpg、mp4です。')
    input.value = ''
    return
  }

  // ファイルサイズチェック（画像8MB未満、動画25MB未満）
  const maxSize = isImage ? 8 * 1024 * 1024 : 25 * 1024 * 1024
  const sizeLabel = isImage ? '8MB' : '25MB'
  const fileTypeLabel = isImage ? '画像' : '動画'

  if (file.size >= maxSize) {
    notifyError(`${fileTypeLabel}ファイルのサイズは${sizeLabel}未満にしてください。`)
    input.value = '' // 入力をクリア
    return
  }

  // 画像/動画ともにAPIアップロードを実行し、mediaId を保存する
  try {
    const result = await store.uploadMedia(file)
    store.updateMediaNode(index, {
      mediaId: result.mediaId,
      url: result.url,
      fileType: isImage ? 'image' : 'video',
      fileName: result.mediaOriginName || file.name
    })
  } catch (error) {
    TrackingService.reportError(
      error,
      {
        feature: 'instagram_instantwin',
        action: 'upload_media',
        screen: 'InstagramScenarioEditorDialog.StepMessage',
        prizeId: typeof store.prizeId === 'number' ? store.prizeId : undefined,
        node: 'message_media',
        fileType: isImage ? 'image' : 'video',
        fileSize: file.size
      },
      { mode: 'always' }
    )
    alert('メディアのアップロードに失敗しました。')
  }

  // 入力をクリア（同じファイルを再選択可能にする）
  input.value = ''
}

// メディアノード: 削除ボタンクリック
const handleMediaDelete = (index: number) => {
  // blob URLを解放
  const message = messages.value[index]
  if (message && message.type === 'media' && message.url && message.url.startsWith('blob:')) {
    URL.revokeObjectURL(message.url)
  }

  // ストアから削除
  store.clearMediaNode(index)
  store.validateStep2()
}

// アンケートノード: 質問テキスト変更
const handleQuestionChange = (index: number, newQuestion: string) => {
  store.updateQuickReplyNode(index, newQuestion, undefined)
  // リアルタイムバリデーション
  store.validateStep2()
}

// アンケートノード: 選択肢のテキスト配列を取得
const getOptionTexts = (index: number): string[] => {
  const message = messages.value[index]
  if (message && message.type === 'select') {
    return message.options.map(opt => opt.text)
  }
  return []
}

// アンケートノード: 選択肢の配列を更新（Tagsコンポーネントから）
const handleOptionsChange = (index: number, newTexts: string[]) => {
  if (campaignRunning.value) {
    return
  }
  const message = messages.value[index]
  if (message && message.type === 'select') {
    // 13個の上限チェック
    if (newTexts.length > 13) {
      // 上限を超える場合は13個までに制限
      newTexts = newTexts.slice(0, 13)
    }

    const newOptions = newTexts.map(text => ({ text }))
    store.updateQuickReplyNode(index, undefined, newOptions)
    // リアルタイムバリデーション
    store.validateStep2()
  }
}

// ============================================
// カードノード用のハンドラー関数
// ============================================

// カードノード: 画像選択ボタンクリック
const handleCardMediaSelectClick = (index: number) => {
  const fileInput = document.getElementById(`cardFileInput-${index}`) as HTMLInputElement
  if (fileInput) {
    fileInput.click()
  }
}

// カードノード: 画像ファイル変更
const handleCardMediaFileChange = async (index: number, event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]

  if (!file) {
    return
  }

  // ファイル形式チェック（png/jpgのみ）
  const imageTypes = ['image/png', 'image/jpeg']
  if (!imageTypes.includes(file.type)) {
    notifyError('対応形式はpng、jpgです。')
    input.value = ''
    return
  }

  if (!isSupportedImageExtension(file.name)) {
    notifyError('対応形式はpng、jpgです。')
    input.value = ''
    return
  }

  // ファイルサイズチェック（8MB未満）
  const maxSize = 8 * 1024 * 1024
  if (file.size >= maxSize) {
    notifyError('画像ファイルのサイズは8MB未満にしてください。')
    input.value = ''
    return
  }

  // APIアップロードを実行
  try {
    const result = await store.uploadMedia(file)
    // アップロード成功時、mediaIdとurlとfileNameを保存
    store.updateCardNode(index, {
      mediaId: result.mediaId,
      url: result.url,
      fileName: result.mediaOriginName || file.name
    })
  } catch (error) {
    TrackingService.reportError(
      error,
      {
        feature: 'instagram_instantwin',
        action: 'upload_card_media',
        screen: 'InstagramScenarioEditorDialog.StepMessage',
        prizeId: typeof store.prizeId === 'number' ? store.prizeId : undefined,
        node: 'message_card',
        fileType: 'image',
        fileSize: file.size
      },
      { mode: 'always' }
    )
    alert('画像のアップロードに失敗しました。')
  }

  // バリデーション
  store.validateStep2()

  // 入力をクリア（同じファイルを再度選択可能にする）
  input.value = ''
}

// カードノード: 画像削除
const handleCardMediaDelete = (index: number) => {
  store.clearCardMedia(index)
  store.validateStep2()
}

// カードノード: タイトル変更
const handleCardTitleChange = (index: number, newTitle: string) => {
  store.updateCardNode(index, { title: newTitle })
  store.validateStep2()
}

// カードノード: サブタイトル変更
const handleCardSubtitleChange = (index: number, newSubtitle: string) => {
  store.updateCardNode(index, { subtitle: newSubtitle })
  store.validateStep2()
}

// カードノード: デフォルトURL変更
const handleCardDefaultUrlChange = (index: number, newUrl: string) => {
  store.updateCardNode(index, { defaultUrl: newUrl })
}

// カードノード: ボタン追加
const handleCardButtonAdd = (index: number) => {
  if (campaignRunning.value) {
    return
  }
  store.addCardButton(index)
  store.validateStep2()
}

// カードノード: ボタン削除
const handleCardButtonDelete = (index: number, buttonIndex: number) => {
  if (campaignRunning.value) {
    return
  }
  store.removeCardButton(index, buttonIndex)
  store.validateStep2()
}

// カードノード: ボタンラベル変更
const handleCardButtonLabelChange = (index: number, buttonIndex: number, newLabel: string) => {
  const message = messages.value[index]
  if (message && message.type === 'card') {
    const newButtons = [...message.buttons]
    newButtons[buttonIndex] = { ...newButtons[buttonIndex], label: newLabel }
    store.updateCardNode(index, { buttons: newButtons })
    store.validateStep2()
  }
}

// カードノード: ボタンURL変更
const handleCardButtonUrlChange = (index: number, buttonIndex: number, newUrl: string) => {
  const message = messages.value[index]
  if (message && message.type === 'card') {
    const newButtons = [...message.buttons]
    newButtons[buttonIndex] = { ...newButtons[buttonIndex], url: newUrl }
    store.updateCardNode(index, { buttons: newButtons })
  }
}

// カードノード: ボタン追加可能かチェック
const canAddCardButton = (index: number): boolean => {
  const message = messages.value[index]
  if (!message || message.type !== 'card') {
    return false
  }
  return message.buttons.length < 3
}

// カードノード: エラー取得関数
const getCardMediaError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.media`] || null
}

const getCardTitleError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.title`] || null
}

const getCardSubtitleError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.subtitle`] || null
}
const getCardDefaultUrlError = (index: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) return null
  return step2Errors[`messages.${index}.defaultUrl`] || null
}

const getCardButtonLabelError = (index: number, buttonIndex: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) {
    return null
  }
  return step2Errors[`messages.${index}.buttons.${buttonIndex}.label`] || null
}
const getCardButtonUrlError = (index: number, buttonIndex: number): string | null => {
  const step2Errors = errors.value[2]
  if (!step2Errors) return null
  return step2Errors[`messages.${index}.buttons.${buttonIndex}.url`] || null
}

const notifyError = (message: string) => {
  proxy.$notify({
    title: message,
    customClass: 'danger',
    duration: 5000
  })
}

/**
 * メッセージノードの変更を監視してリアルタイムバリデーション
 */
watch(
  messages,
  () => {
    store.validateStep2()
  },
  { deep: true }
)

return { __sfc: true,proxy, store, messages, canAddMessage, errors, campaignRunning, isAddMessageDisabled, showLimitMessage, getFileExtension, isSupportedImageExtension, canDeleteMessage, canChangeToType, getTextNodeError, getQuestionError, getOptionError, hasSelectOptionsErrors, getOptionsCountError, getMediaNodeError, isImageFile, getMediaFileName, getCardButtonPreviewLabel, handleAddMessage, handleDelete, handleTypeChange, handleTextChange, handleMediaSelectClick, handleMediaFileChange, handleMediaDelete, handleQuestionChange, getOptionTexts, handleOptionsChange, handleCardMediaSelectClick, handleCardMediaFileChange, handleCardMediaDelete, handleCardTitleChange, handleCardSubtitleChange, handleCardDefaultUrlChange, handleCardButtonAdd, handleCardButtonDelete, handleCardButtonLabelChange, handleCardButtonUrlChange, canAddCardButton, getCardMediaError, getCardTitleError, getCardSubtitleError, getCardDefaultUrlError, getCardButtonLabelError, getCardButtonUrlError, notifyError, HelpTooltip, Tags }
}

})